import React from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { cn } from 'lib/utils'

interface DynamicListProps {
  items: string[]
  onChange: (items: string[]) => void
  onBlur?: () => void
  placeholder?: string
  label?: string
  required?: boolean
  error?: string
  description?: string
  className?: string
  maxItems?: number
  minItems?: number
  allowAdd?: boolean
  allowEdit?: boolean
  allowDelete?: boolean
}

export function DynamicList({
  items,
  onChange,
  onBlur,
  placeholder = '',
  label,
  required = false,
  error,
  description,
  className,
  maxItems = 10,
  minItems = 1,
  allowAdd = true,
  allowEdit = true,
  allowDelete = true
}: DynamicListProps) {
  const { t } = useTranslation()

  const addItem = () => {
    if (items.length < maxItems) {
      onChange([...items, ''])
    }
  }

  const removeItem = (index: number) => {
    if (!allowDelete) return
    if (items.length > minItems) {
      const newItems = items.filter((_, i) => i !== index)
      onChange(newItems)
    }
  }

  const updateItem = (index: number, value: string) => {
    if (!allowEdit) return
    const newItems = [...items]
    newItems[index] = value
    onChange(newItems)
  }

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
      
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="flex items-center space-x-2">
            <Input
              value={item}
              onChange={(e) => updateItem(index, e.target.value)}
              onBlur={onBlur}
              placeholder={placeholder}
              readOnly={!allowEdit}
              className={error && !item.trim() ? "border-destructive" : ""}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => removeItem(index)}
              disabled={!allowDelete || items.length <= minItems}
              className="h-10 w-10 p-0 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}

        {allowAdd && (
          <Button
            type="button"
            variant="outline"
            onClick={addItem}
            disabled={items.length >= maxItems}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('common.addItem', 'Add item')}
          </Button>
        )}
      </div>
      
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  )
}
